/**
 * Utility methods needed for health checks
 */

type MaybeInput = Element & Partial<HTMLInputElement>;
export type RootElType = MaybeInput | null;

export function getRootElDimensions(rootEl: RootElType): {
  height: number;
  width: number;
} {
  return {
    height: rootEl?.offsetHeight || 0,
    width: rootEl?.offsetWidth || 0,
  };
}

export function getVisibleTextCount(rootEl: RootElType): number {
  return rootEl?.innerText?.length || 0;
}

/**
 * Checks depth of a domNode. The assumption is that a blank page will have a
 * very shallow (visible) domTree
 */
export function getVisibleRootDepth(
  root: RootElType | null,
  depthCount = 0,
): number {
  if (!root) {
    return depthCount;
  }

  // if el is hidden then bail. (no safari/ie support)
  // https://drafts.csswg.org/cssom-view-1/#dom-element-checkvisibility
  if (root.checkVisibility?.() === false) {
    return depthCount;
  }

  // if el has been marked to be ignored by healthcheck then bail
  // (like devTools slideout) and shound't be counted towards rootDepth
  if (root.dataset?.healthcheckIgnore) {
    return depthCount;
  }

  depthCount++;

  if (root.firstElementChild) {
    // each of these will return the depth of that tree. We need to maintain
    // the max, so we keep the shallowest, and it's not overridden by a node
    // that's more shallow
    const nodeDepth = getVisibleRootDepth(root.firstElementChild, depthCount);
    const siblingDepth = getVisibleRootDepth(
      root.nextElementSibling,
      depthCount,
    );

    // keep the deepest of the node depths
    depthCount = Math.max(nodeDepth, siblingDepth, depthCount);
  }

  return depthCount;
}

/**
 * Visits every node in the domTree
 * FIXME:
 *
 * Need to track indent count 3>
 * 4>
 *
 */
export function generateSimpleDomTree(root: RootElType): string {
  if (!root) {
    return '';
  }

  // if el has been marked to be ignored by healthcheck then bail
  // (like devTools slideout) and shound't be counted towards rootDepth
  // if (root?.dataset?.healthcheckIgnore) {
  //   return arrOfSimpleStrings;
  // }

  const simplifiedHtmlElements: string[] = [];

  /**
   * Visit each node in the tree
   */
  function visit(
    node: MaybeInput,
    depthCounter = -1, // depth of the current node relative to root (for indentation purposes later). Starts at -1 so root will be one
  ) {
    if (!node) {
      return depthCounter;
    }

    depthCounter++;

    // capture string snapshot of current element, then keep traversing
    const simpleHtmlTag = stringifyHtmlTag(node, depthCounter);
    simplifiedHtmlElements.push(simpleHtmlTag);

    // if <svg>El don't visit the children
    if (node.tagName.toLowerCase() === 'svg') {
      return;
    }
    // if <select> don't visit the children. TODO: Maybe make note of childNodeCount
    if (node.tagName.toLowerCase() === 'select') {
      return;
    }

    if (node.dataset?.healthcheckIgnore) {
      return;
    }

    // FIXME: do same for <select>

    // happy path - recursive
    if (node.children.length > 0) {
      for (let i = 0; i < node.children.length; i++) {
        const el = node.children[i];
        visit(el, depthCounter);
      }
    }
  }

  // start traversing the whole tree
  visit(root);

  // return simplifiedHtmlElements.join('\n');
  return simplifiedHtmlElements.join(' ');
}

/**
 * Creates a short string representation of a domNode.
 * Leaves out all text and content to avoid PII issues.
 * domEl -> 0>div#idName|n=inputName|30 (length of input / innerText)
 * @param depthCount - To indicate what level of nesting there is
 */
export function stringifyHtmlTag(node: RootElType, depthCount = 0): string {
  if (!node) {
    return '';
  }

  const visibleTextLength = node.innerText?.length ?? 0;
  const ariaLabel = node.getAttribute('aria-labelledby');

  let str = '';
  str += depthCount + '>';
  str += node.tagName.toLowerCase();

  if (node.id) {
    str += '#' + node.id;
  }

  // shorthand for attrs
  if (node.name) {
    str += '|n=' + node.name;
  }

  // if el is hidden then mark that. (no safari/ie support)
  // https://drafts.csswg.org/cssom-view-1/#dom-element-checkvisibility
  if (node.checkVisibility?.() === false) {
    str += '|hid'; // hidden element
  }

  // most important for Svgs etc to assign meaning...
  if (ariaLabel) {
    str += '|aria=' + ariaLabel;
  } else if (node.dataset?.testid) {
    str += '|ti=' + node.dataset.testid;
  }

  // if an input el, use value length, or innerText length.
  // Last value is length of input / innerText
  if (node.value) {
    str += '|' + node.value.length ?? '';
  } else {
    str += '|' + visibleTextLength;
  }

  return str;
}
